import {
  Database,
  Engine,
  init_Offline,
  init_engine
} from "./chunk-WLPEHRZS.mjs";
import {
  isIOSDevice
} from "./chunk-UZ5OU5IO.mjs";
import {
  Scene,
  ScenePerformancePriority,
  init_scene
} from "./chunk-XEBZFSO2.mjs";
import {
  RenderingGroup,
  init_renderingGroup
} from "./chunk-KU6MOYBB.mjs";
import {
  Color4,
  init_math_color
} from "./chunk-LWSYNDGD.mjs";
import {
  Constants_default
} from "./chunk-XBSQY4D4.mjs";
import {
  Queries
} from "./chunk-J34LIYA7.mjs";
import {
  __async
} from "./chunk-AU4RG7EN.mjs";

// src/modules/core/SceneManager.ts
init_scene();
init_math_color();
init_engine();
init_renderingGroup();
init_Offline();
var CAPPED_FPS = 60;
var SceneManager = class {
  constructor() {
    this.renderEventTimer = Constants_default.MAX_RENDER_TIME;
    this.renderEvent = true;
    this.lastTime = performance.now();
    this.interval = 1e3 / CAPPED_FPS;
  }
  initScene(payload, canvasContainer) {
    return __async(this, null, function* () {
      this.engine = new Engine(
        canvasContainer,
        true,
        {
          preserveDrawingBuffer: isIOSDevice() ? false : true,
          stencil: isIOSDevice() ? false : true,
          powerPreference: isIOSDevice() ? "low-power" : "default",
          useHighPrecisionFloats: isIOSDevice() ? false : true,
          useHighPrecisionMatrix: isIOSDevice() ? false : true
        },
        isIOSDevice() ? false : Constants_default.ADAPT_TO_DEVICE_RATIO
      );
      this.engine.enableOfflineSupport = false;
      const assetCacheVersion = payload.assetVersionKey;
      const existingCacheVersion = localStorage.getItem("assetCacheVersion");
      const resetSceneCaching = assetCacheVersion && assetCacheVersion !== existingCacheVersion;
      let isIndexedDBReady = true;
      if (payload.aggressiveCachingEnabled && "indexedDB" in window) {
        if (resetSceneCaching || Queries.isClearCacheInQuery()) {
          isIndexedDBReady = yield this.deleteCacheDatabase();
          localStorage.setItem("assetCacheVersion", assetCacheVersion);
        }
        if (isIndexedDBReady && !resetSceneCaching && !Queries.isClearCacheInQuery()) {
          console.log("[Engine3D][IndexedDB] Asset caching is enabled.");
          this.engine.enableOfflineSupport = true;
          Database.IDBStorageEnabled = true;
        }
      }
      this.engine.disableManifestCheck = true;
      this.engine.disablePerformanceMonitorInBackground = true;
      if (isIOSDevice()) {
        this.engine.enableOfflineSupport = false;
        this.engine.disableUniformBuffers = true;
        this.engine.getCaps().maxMSAASamples = payload.maxMSAASamples || 4;
        this.engine.getCaps().highPrecisionShaderSupported = false;
        this.engine.setHardwareScalingLevel(payload.hardwareScaling || 0.75);
      } else {
        this.engine.setHardwareScalingLevel(0.5);
      }
      this.scene = new Scene(this.engine);
    });
  }
  initNewScene(payload, canvasContainer) {
    return __async(this, null, function* () {
      this.payload = payload;
      if (this.engine)
        this.dispose();
      yield this.initScene(payload, canvasContainer);
      if (!this.scene)
        return console.error("Scene not defined");
      this.scene.preventDefaultOnPointerDown = false;
      this.scene.preventDefaultOnPointerUp = false;
      this.scene.blockMaterialDirtyMechanism = true;
      this.scene.clearColor = new Color4(0, 0, 0, 0);
      this.scene.skipFrustumClipping = false;
      this.scene.setRenderingOrder(
        0,
        null,
        null,
        RenderingGroup.PainterSortCompare
      );
      this.scene.pointerMovePredicate = (mesh) => {
        return mesh.isPickable && mesh.isVisible && mesh.isReady() && mesh.isEnabled();
      };
      if (isIOSDevice()) {
        this.scene.clearCachedVertexData();
        this.scene.cleanCachedTextureBuffer();
      }
      if (payload.loveIOS) {
        this.scene.performancePriority = ScenePerformancePriority.Intermediate;
      }
    });
  }
  applyImageProcessing(imageProcessingProperties) {
    if (!this.scene || !imageProcessingProperties) {
      console.error("Scene not defined");
      return;
    }
    if (imageProcessingProperties.toneMapping === 0) {
      this.scene.imageProcessingConfiguration.toneMappingEnabled = false;
    } else {
      this.scene.imageProcessingConfiguration.toneMappingEnabled = true;
      this.scene.imageProcessingConfiguration.toneMappingType = imageProcessingProperties.toneMapping - 1;
    }
    this.scene.imageProcessingConfiguration.contrast = imageProcessingProperties.contrast;
    this.scene.imageProcessingConfiguration.exposure = imageProcessingProperties.exposure;
    this.scene.environmentIntensity = imageProcessingProperties.iblIntensity;
  }
  dispose() {
    if (!this.scene || !this.engine) {
      console.error("Scene or Engine not defined");
      return;
    }
    this.scene.onPointerObservable.clear();
    this.scene.onKeyboardObservable.clear();
    this.scene.dispose();
    this.engine.stopRenderLoop();
    this.engine.dispose();
    this.engine = void 0;
  }
  renderScene(value = 0) {
    var _a;
    this.renderEventTimer = value;
    if (Constants_default.CONTINUOUS_RENDERING && !((_a = this.payload) == null ? void 0 : _a.frugalRenderLoop))
      this.renderEventTimer = -1;
    this.renderEvent = true;
  }
  startRendering() {
    if (!this.scene || !this.engine) {
      console.error("Scene or Engine not defined");
      return;
    }
    this.renderLoop(this.engine);
    return;
  }
  renderLoop(engine) {
    var _a;
    if (!engine)
      return;
    engine.beginFrame();
    let currentTime = performance.now();
    const deltaTime = currentTime - this.lastTime;
    if (deltaTime > this.interval) {
      this.lastTime = currentTime - deltaTime % this.interval;
      (_a = this.scene) == null ? void 0 : _a.render();
    }
    engine.endFrame();
    requestAnimationFrame(() => this.renderLoop(engine));
  }
  resize() {
    if (this.engine) {
      this.engine.resize();
    }
    this.renderScene();
  }
  debugLayerShow() {
    if (!this.scene)
      return;
    return import("./helpers/Debugger.mjs").then((debugModule) => {
      return debugModule.toggleDebugMode(this.scene);
    });
  }
  deleteCacheDatabase() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.deleteDatabase("babylonjs");
      request.onsuccess = function(event) {
        resolve(true);
        console.log(
          "[Engine3D][IndexedDB] Cache database deleted successfully."
        );
      };
      request.onerror = function(event) {
        resolve(false);
        console.error(
          "[Engine3D][IndexedDB] Cache database deletion error:",
          event.target.error
        );
      };
      request.onblocked = function(event) {
        resolve(false);
        console.log(
          "[Engine3D][IndexedDB] Cache database deletion is blocked."
        );
      };
    });
  }
};

export {
  SceneManager
};
